/* eslint-disable react/no-this-in-sfc */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo.svg';
import googleLogo from '../../assets/images/google-logo.svg';
import eyeIcon from '../../assets/images/eye.svg';
import eyeCloseIcon from '../../assets/images/eye-close.png';
import commonUtils from '../../utils/common/common';
// import dashboardImg from '../../assets/images/Dashboard.png';
// import analyticsDashboardImg from '../../assets/images/Web-Analytics-Dashboard.png';
import { getGoogleUrl } from '../../utils/google';
import framework1 from '../../assets/images/framework1.svg';
import framework2 from '../../assets/images/framewor2.svg';
import framework3 from '../../assets/images/framewor3.svg';
import userService from '../../services/user.service';
import EmailVerificationPage from '../emailVerification/EmailVerification';
import NavigateSnackbar from '../navigateSnackbar/NavigateSnackbar';
import { DashboardEnum } from '../../utils/constants';
import ReCaptcha from '../ReCaptcha';
import useMeta from '../../utils/useMeta';

const SignupPage = () => {
  useMeta('Sign Up', 'Create your TestReport.io account and start managing your software testing projects today.');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(DashboardEnum.SELENIUM);
  const [token, setToken] = useState();
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const onVerify = useCallback((captchaToken) => {
    if (!token) setToken(captchaToken);
  }, [refreshReCaptcha, token]);

  const [navigateSnackbarState, setNavigateSnackbarState] = useState({
    navigateText: '',
    severity: '',
    state: false,
    message: '',
  });
  document.body.classList.add('loginBody');

  Yup.addMethod(Yup.string, 'sanitizeInput', function sanitizeInput() {
    return this.transform((value) => {
      if (typeof value === 'string') {
        const sanitized = commonUtils.sanitizeInput(value);
        return sanitized;
      }
      return value;
    });
  });

  const signupSchema = Yup.object({
    firstname: Yup.string()
      .sanitizeInput()
      .trim()
      .min(3, 'First name must be at least 3 characters')
      .matches(/^[A-Za-z\s_]*$/, 'First name should not contain numbers or special characters')
      .required('Please enter your first name'),

    lastname: Yup.string()
      .sanitizeInput()
      .trim()
      .min(3, 'Last name must be at least 3 characters')
      .matches(/^[A-Za-z\s_]*$/, 'Last name should not contain numbers or special characters')
      .required('Please enter your last name'),

    email: Yup.string()
      .trim()
      .matches(commonUtils.validateEmail, 'Email is invalid')
      .test(
        'not-disposable',
        'Temporary email addresses are not allowed. Please use a valid email to register.',
        (value) => commonUtils.isDisposableEmail(value) === false,
      )
      .required('Please enter your email'),
    password: Yup.string()
      .trim()
      .required('Password is required')
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/,
        'Password must be at least 8 characters, include an uppercase letter, a lowercase letter, a number, a special character, and have no spaces',
      ),
    confirmpassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password'),
    agreeTerms: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    companyName: Yup.string()
      .sanitizeInput()
      .trim()
      .min(3, 'Company name must be at least 3 characters')
      .matches(/^[A-Za-z\s_]*$/, 'Company name should not contain numbers or special characters')
      .required('Company Name is required'),
    project: Yup.string()
      .sanitizeInput()
      .trim()
      .min(3, 'Project name must be at least 3 characters')
      .matches(/^[A-Za-z\s_]*$/, 'Project name should not contain numbers or special characters')
      .required('Project Name is required'),
  });

  const formik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      confirmpassword: '',
      agreeTerms: false,
      companyName: '',
      project: '',
      dashboardType: '',
    },
    validationSchema: signupSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        const res = await userService.userRegister({
          email: values.email,
          password: values.password,
          firstName: commonUtils.sanitizeInput(values.firstname),
          lastName: commonUtils.sanitizeInput(values.lastname),
          framework: selectedFramework || values.dashboardType,
          project: commonUtils.sanitizeInput(values.project),
          companyName: commonUtils.sanitizeInput(values.companyName),
        }, token);
        setVerifyEmail(res);
        setEmail(values.email);
      } catch (error) {
        setNavigateSnackbarState({
          navigateText: '',
          severity: 'error',
          state: true,
          message: `${error?.data?.message || 'An error occurred'}`,
        });
      } finally {
        setToken(null);
        setRefreshReCaptcha(!refreshReCaptcha);
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };
  const handleConfirmPasswordToggle = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleFrameworkSelect = (framework) => {
    setSelectedFramework(framework);
    formik.setFieldValue('dashboardType', framework);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setToken(null);
      setRefreshReCaptcha((prev) => !prev);
    }, 110000);

    return () => clearInterval(intervalId);
  }, [refreshReCaptcha]);

  return (
    <>
      {verifyEmail ? (
        <EmailVerificationPage email={email} />
      ) : (
        <div className="container-fluid signup-main pt-3">
          <div className="row">
            <div className="col-sm-12" style={{ overflow: 'auto' }}>
              <div className="left-block-details pt-50">
                <div className="logo-block-wrap">
                  <a href="https://testreport.io/">
                    <img src={logo} alt="logo" width="206" height="38" />
                  </a>
                </div>

                <div className="login-detail-block">
                  <div className="login-detail-inner">
                    <div className="login-detail-block">
                      <h2 className="h2">
                        Welcome to
                        <b>
                          {' testreport '}
                        </b>
                      </h2>
                      <p className="sub-title">Enter your signup details below</p>
                      <form onSubmit={formik.handleSubmit}>
                        <button onClick={() => { window.location.href = getGoogleUrl(); }} className="google-btn" type="button">
                          <img src={googleLogo} alt="google logo" width="28" height="28" />
                          Signup with Google
                        </button>
                        <span className="or-boder-block" />
                        <div className="d-block d-sm-flex justify-content-between mt-4">
                          <div className="input-wrap input-width me-1">
                            <label className="label" htmlFor="firstname">
                              First Name
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Enter your first name"
                              name="firstname"
                              id="firstname"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.firstname}
                            />
                            {formik.touched.firstname && formik.errors.firstname && (
                              <span className="error-msg">{formik.errors.firstname}</span>
                            )}
                          </div>
                          <div className="input-wrap input-width ms-1">
                            <label className="label" htmlFor="lastname">
                              Last Name
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Enter your last name"
                              name="lastname"
                              id="lastname"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.lastname}
                            />
                            {formik.touched.lastname && formik.errors.lastname && (
                              <span className="error-msg">{formik.errors.lastname}</span>
                            )}
                          </div>
                        </div>
                        <div className="input-wrap">
                          <label className="label" htmlFor="email">
                            Email Address
                            {' '}
                            <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className="form-control"
                            type="email"
                            placeholder="Enter your email"
                            name="email"
                            id="email"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <span className="error-msg">{formik.errors.email}</span>
                          )}
                        </div>
                        <div className="d-block d-sm-flex justify-content-between mb-4">
                          <div className="input-wrap input-width me-1">
                            <label className="label" htmlFor="password">
                              Password
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <div className="password-wrap">
                              <input
                                className="form-control password-bk"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                name="password"
                                id="password"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                              />
                              <div
                                className="password-show"
                                tabIndex="0"
                                role="button"
                                onClick={handlePasswordToggle}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    handlePasswordToggle();
                                  }
                                }}
                              >
                                <img
                                  className="eye-show"
                                  src={showPassword ? eyeCloseIcon : eyeIcon}
                                  alt="toggle visibility"
                                  width="20"
                                  height="20"
                                />
                              </div>
                            </div>
                            {formik.touched.password && formik.errors.password && (
                              <span className="error-msg">{formik.errors.password}</span>
                            )}
                          </div>
                          <div className="input-wrap input-width ms-1">
                            <label className="label" htmlFor="confirmpassword">
                              Confirm Password
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <div className="password-wrap">
                              <input
                                className="form-control password-bk"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Enter your confirm password"
                                name="confirmpassword"
                                id="confirmpassword"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.confirmpassword}
                              />
                              <div
                                className="password-show"
                                tabIndex="0"
                                role="button"
                                onClick={handleConfirmPasswordToggle}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    handleConfirmPasswordToggle();
                                  }
                                }}
                              >
                                <img
                                  className="eye-show"
                                  src={showConfirmPassword ? eyeCloseIcon : eyeIcon}
                                  alt="toggle visibility"
                                  width="20"
                                  height="20"
                                />
                              </div>
                            </div>
                            {formik.touched.confirmpassword && formik.errors.confirmpassword && (
                              <span className="error-msg">{formik.errors.confirmpassword}</span>
                            )}
                          </div>
                        </div>
                        <div className="d-block d-sm-flex justify-content-between">
                          <div className="input-wrap input-width me-1">
                            <label className="label" htmlFor="companyName">
                              Company Name
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Enter Company Name"
                              name="companyName"
                              id="companyName"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.companyName}
                            />
                            {formik.touched.companyName && formik.errors.companyName && (
                              <span className="error-msg">{formik.errors.companyName}</span>
                            )}
                          </div>
                          <div className="input-wrap input-width ms-1">
                            <label className="label" htmlFor="project">
                              Project
                              {' '}
                              <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Enter Project name"
                              name="project"
                              id="project"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.project}
                            />
                            {formik.touched.project && formik.errors.project && (
                              <span className="error-msg">{formik.errors.project}</span>
                            )}
                          </div>
                        </div>
                        <div className="select-framework-wrap">
                          <p>Select Framework</p>
                          <div className="select-framework-row">
                            <div
                              className={`framework-box ${selectedFramework === `${DashboardEnum.SELENIUM}` ? 'active' : ''}`}
                              onClick={() => handleFrameworkSelect(DashboardEnum.SELENIUM)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleFrameworkSelect(DashboardEnum.SELENIUM);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              onBlur={() => formik.setFieldTouched('dashboardType', true)}
                            >
                              <img src={framework1} alt="framework1" width="85" height="85" />
                              <span>Selenium</span>
                            </div>
                            <div
                              className={`framework-box ${selectedFramework === `${DashboardEnum.PLAYWRIGHT}` ? 'active' : ''}`}
                              onClick={() => handleFrameworkSelect(DashboardEnum.PLAYWRIGHT)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleFrameworkSelect(DashboardEnum.PLAYWRIGHT);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              onBlur={() => formik.setFieldTouched('dashboardType', true)}
                            >
                              <img src={framework2} alt="framework2" width="85" height="85" />
                              <span>Playwright</span>
                            </div>
                            <div
                              className={`framework-box ${selectedFramework === `${DashboardEnum.CYPRESS}` ? 'active' : ''}`}
                              onClick={() => handleFrameworkSelect(DashboardEnum.CYPRESS)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleFrameworkSelect(DashboardEnum.CYPRESS);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              onBlur={() => formik.setFieldTouched('dashboardType', true)}
                            >
                              <img src={framework3} alt="framework3" width="85" height="85" />
                              <span>Cypress</span>
                            </div>
                          </div>
                          {formik.errors.dashboardType && formik.touched.dashboardType && (
                            <span className="error-msg">{formik.errors.dashboardType}</span>
                          )}
                        </div>
                        <div className="input-wrap-check">
                          <div className="checkbox-lecf">
                            <input
                              type="checkbox"
                              id="agreeTerms"
                              name="agreeTerms"
                              onChange={formik.handleChange}
                              checked={formik.values.agreeTerms}
                            />
                            <label htmlFor="agreeTerms">I agree to all Terms, Privacy Policy, and Fees</label>
                          </div>
                          {formik.touched.agreeTerms && formik.errors.agreeTerms && (
                            <span className="error-msg" style={{ position: 'relative', display: 'block', marginTop: '8px' }}>
                              {formik.errors.agreeTerms}
                            </span>
                          )}
                        </div>
                        <button className="custom-btn" type="submit" disabled={formik.isSubmitting || loading || !token}>
                          {loading ? 'Registering...' : 'Register Now'}
                        </button>
                        <p className="bottom-link-wrap">
                          Already have an account?
                          <Link to="/login" style={{ marginLeft: 8 }}>Log in</Link>
                        </p>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="col-sm-12 col-lg-6">
              <div className="right-block-details right-block-details-signup">
                <div className="login-img-right">
                  <h1 className="h1">The simplest way to manage your workforce</h1>
                  <p>Enter your credentials to access your account</p>
                  <div className="right-block-detailsimg">
                    <img className="img-ds"
                    src={analyticsDashboardImg} alt="Dashboard" width="400" height="350" />
                    <img className="sd-img"
                    src={dashboardImg} alt="Web-Analytics-Dashboard" width="640" height="420" />
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      )}
      <ReCaptcha onVerify={onVerify} refreshReCaptcha={refreshReCaptcha} action="SignUp" />
      {
        navigateSnackbarState.state
        && (
          <NavigateSnackbar
            snackbarObj={navigateSnackbarState}
            setSnackbar={setNavigateSnackbarState}
          />
        )
      }
    </>
  );
};

export default SignupPage;
